import useTranslation from "next-translate/useTranslation";
import React, { useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { TheCycleverseTheme } from "src/theme";
import { BoldParagraphWithOrangeText, CalculatorBodyText } from "../CalculatorTypography";
import { CalculatorButtonWithWidth } from "./CalculatorButton";
import { ErrorMessage } from "src/components/common/ErrorMessage";
import { InputComponent } from "../CalculatorSelectInput";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  errorMessage: {
    fontSize: "16px",
    color: theme.calculatorOrangeBoldColor,
  },

  paragraphWithDifferentSyles: {
    fontSize: "16px",
    fontWeight: 300,
    fontFamily: theme.typography.fontFamily,
    lineHeight: "24px",
    margin: 0,
  },
  orangeBoldText: {
    color: theme.calculatorOrangeBoldColor,
    fontWeight: 600,
  },

  bold: {
    fontWeight: 600,
  },

  calculatorContainer: {
    display: "flex",
    alignItems: "center",
    gap: "50px",
    flexWrap: "wrap",
    "@media (max-width: 451px)": {
      flexDirection: "column",
      gap: "10px",
    },
  },

  marginTop10: {
    marginTop: "10px",
  },

  marginTop20: {
    marginTop: "20px",
  },

  marginTop30: {
    marginTop: "30px",
  },
}));

const Calculator = () => {
  const classes = useStyles();
  const { t, lang } = useTranslation("lumenLuxRechner");
  const [result, setResult] = useState(false);
  const [fields, setFields] = useState({ field1: null, field2: null, field3: null, field4: null });
  const [error, setError] = useState(false);

  const convertToFloatNumber = (field) => {
    return field.replace(",", ".");
  };

  const calculateFeild1 = () => {
    // field 1 = (field 4 / field 3) / (field 2) ^ 2
    const { field2, field3, field4 } = fields;
    if (field2 && field3 && field4) {
      const endResult = Number(
        (
          convertToFloatNumber(field4) /
          convertToFloatNumber(field3) /
          Math.pow(convertToFloatNumber(field2), 2)
        ).toFixed(2)
      ).toLocaleString(lang);

      setFields({ field1: endResult, field2: null, field3: null, field4: null });
      setResult(true);
    } else {
      setError(true);
    }
  };

  const calculateFeild2 = () => {
    // field 4 = field 1 * field 3 * (field 2) ^ 2
    const { field1, field2, field3 } = fields;
    if (field1 && field2 && field3) {
      const endResult = (
        convertToFloatNumber(field1) *
        convertToFloatNumber(field3) *
        Math.pow(convertToFloatNumber(field2), 2)
      ).toLocaleString(lang);

      setFields({ field4: endResult, field2: null, field3: null, field1: null });
      setResult(true);
    } else {
      setError(true);
    }
  };

  const handleInputChange = (event) => {
    setError(false);
    const { value, name } = event.target;
    if (result) {
      setFields({ ...fields, [name]: null });
      setResult(false);
    }

    const onlyNumbersRegexDE = /^[0-9]*([,][0-9]*)?$/;
    const onlyNumbersRegexEN = /^[0-9]*([.][0-9]*)?$/;
    if (lang === "de" && onlyNumbersRegexDE.test(value)) {
      setFields({ ...fields, [name]: value });
    } else if (lang === "en" && onlyNumbersRegexEN.test(value)) {
      setFields({ ...fields, [name]: value });
    } else {
      setFields({ ...fields, [name]: null });
    }
  };

  return (
    <>
      {error ? <ErrorMessage message={t("errorMessage")} className={classes.errorMessage} /> : null}
      <div className={classes.calculatorContainer}>
        <div>
          <BoldParagraphWithOrangeText>
            <span>{t("field1")}</span>
            <span className={classes.orangeBoldText}>{` (${t("textInOrange2")})`}</span>
          </BoldParagraphWithOrangeText>

          <div className={classes.marginTop10}>
            <InputComponent result={result} name="field1" onChange={handleInputChange} value={fields.field1 || ""} />
          </div>

          <div className={classes.marginTop20}>
            <BoldParagraphWithOrangeText>{t("field2")}</BoldParagraphWithOrangeText>
            <div className={classes.marginTop10}>
              <InputComponent result={result} name="field2" onChange={handleInputChange} value={fields.field2 || ""} />
            </div>
          </div>

          <div className={classes.marginTop20}>
            <BoldParagraphWithOrangeText>{t("field3")}</BoldParagraphWithOrangeText>
            <div className={classes.marginTop10}>
              <InputComponent result={result} name="field3" onChange={handleInputChange} value={fields.field3 || ""} />
            </div>
          </div>

          <div className={classes.marginTop20}>
            <BoldParagraphWithOrangeText>
              <span className={classes.bold}>{t("field4")}</span>
              <span className={classes.orangeBoldText}>{` (${t("textInOrange1")})`}</span>
            </BoldParagraphWithOrangeText>
          </div>

          <div className={classes.marginTop10}>
            <InputComponent result={result} name="field4" onChange={handleInputChange} value={fields.field4 || ""} />
          </div>
        </div>

        <div>
          <div className={classes.marginTop30}>
            <CalculatorBodyText>{t("calculatorButtonsTitle")}</CalculatorBodyText>
          </div>
          <CalculatorButtonWithWidth btnText={t("calculatorButton1")} calculate={calculateFeild1} />
          <CalculatorButtonWithWidth btnText={t("calculatorButton2")} calculate={calculateFeild2} />
        </div>
      </div>
    </>
  );
};

export default Calculator;
